import React, {useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import './index.css';
import {Field, Form, Formik} from 'formik';
import SimpleButtonGroup from '../../../../../components/formik/SimpleButtonGroup';
import {history} from '../../../../../utils/redux/store';
import InfoDialog from '../../../../../components/dialogs/InfoDialog';
import skincareProDao from '../../../../../dao/skincareProDao';
import {useSelector} from 'react-redux';
import SelectBox from '../../../../../components/formik/SelectBox';
import ProductSetting from '../../../components/customFormik/productSetting';
import {toast} from 'react-toastify';
import DragTable from '../../../../../components/dragdatatable/DragTable';
import FormattedDate from '../../../../../components/product/FormattedDate';
import ActionButton from '../../../../../components/datatable/ActionButton';
import Loader from '../../../../../components/spinner/Loader';
import ChooseProductModal from './ChooseProductModal';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import smbUtils from '../../../../../utils/smbUtils';
import {privilegeUtils} from '../../../../../utils/privilegeUtils';
import langUtils from '../../../../../utils/langUtils';
import langCode from '../../../../../utils/langUtils';
import _get from 'lodash/get';

const ControlBar = ({setShowModal}) => {
  return (
    <div className="control-bar">
      <button
        type="button"
        className="btn btn-danger kt-margin-b-10"
        onClick={() => {
          setShowModal(true);
        }}
      >
        <i className="fa fa-plus" />
        <Trans i18nKey="p.s.addoreditskincareroutine.choose.skincare.product">
          Choose Skincare Product
        </Trans>
      </button>
    </div>
  );
};

const getI18nData = () => {
  const i18n = {};
  const i18nColumns = {productName: '', productDesc: '', isDefault: false};

  for (const lang of langUtils.getAppLangList()) {
    i18n[lang.value] = i18nColumns;
  }
  return i18n;
};

export default function AddOrEditSkincareRoutine() {
  const [isLoaded, setIsLoaded] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [initValue, setInitValue] = useState({
    isDefault: 'enu',
    data: {
      i18n: getI18nData(),
    },
  });
  const [selectedData, setSelectedData] = useState([]);
  const accountSummary = useSelector(
    state => state.components.auth.accountSummary
  );
  const {token, customerId} = smbUtils.customerInfo(accountSummary);
  const brandName = smbUtils.getCustomerName(accountSummary);
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const productId = params.get('productId');
  const {t} = useTranslation();

  const getEditedRoutineAPI = async productId => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('productIds', productId);
    tmp.append('showDataAndFiles', true);
    tmp.append('showProductRefs', true);
    const res = await skincareProDao.getGenericInfo(tmp);

    if (res && res[0]) {
      const i18nKeys = Object.keys(res[0].data.i18n);
      const defaultKey = i18nKeys.find(key => res[0].data.i18n[key].isDefault);

      const i18nFormattedData = {};

      for (const lang of langUtils.getAppLangList()) {
        i18nFormattedData[lang.value] = res[0].data.i18n[lang.value] || {
          productName: '',
          isDefault: false,
        };
      }

      setInitValue({
        isDefault: defaultKey,
        data: {
          i18n: i18nFormattedData,
        },
      });

      const transformedData = res[0].productRefs.map(item => {
        const i18nKey = Object.keys(item.product.data.i18n)[0];
        return {
          id: item.product.id,
          categoryId: item.product.categoryIds,
          name: item.product.name,
          imageUrl: item.product.imageUrl,
          actionUrl: item.product.data.i18n[i18nKey].actionUrl,
          createdTime: item.product.createdTime,
          lastModified: item.product.lastModified,
          status: item.product.status,
        };
      });

      const displayArray = res[0].data.productRefs;

      //Show UI order from small to large according to displayOrder
      const sortedTransformedData = transformedData.sort((a, b) => {
        const displayOrderA = displayArray.find(
          item => item.referencedProduct.id === a.id
        ).displayOrder;
        const displayOrderB = displayArray.find(
          item => item.referencedProduct.id === b.id
        ).displayOrder;
        return displayOrderA - displayOrderB;
      });

      setSelectedData(sortedTransformedData);
      return;
    }
    toast.error(
      <Trans i18nKey="toast.error.loading.data.failed">
        Failed to load data.
      </Trans>
    );
    history.push('/skincare-pro/add-skincare/skincare-routine');
  };

  const createRoutineAPI = async apiValue => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('type1', 'Store');
    tmp.append('type2', 'SMB');
    tmp.append('type3', 'SMB_ROUTINE');

    apiValue.data.productSource = 'BRAND';

    tmp.append('data', JSON.stringify(apiValue.data));
    return await skincareProDao.createGenericList(tmp);
  };

  const updateRoutineAPI = async apiValue => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('type1', 'Store');
    tmp.append('type2', 'SMB');
    tmp.append('type3', 'SMB_ROUTINE');
    tmp.append('productId', productId);

    apiValue.data.productSource = 'BRAND';

    tmp.append('data', JSON.stringify(apiValue.data));
    return await skincareProDao.updateGenericList(tmp);
  };

  useEffect(() => {
    setIsLoaded(true);
    const getInitValue = async () => {
      if (productId) {
        await getEditedRoutineAPI(productId);
      }
      setIsLoaded(false);
    };
    getInitValue();
  }, [productId]);

  const columns = [
    {
      Header: <Trans i18nKey="p.s.addoreditskincareroutine.no">No.</Trans>,
      accessor: 'order',
      disableSortBy: true,
      style: {width: '5%', textAlign: 'center'},
      Cell: ({cell}) => (
        <div className="text-center">{Number(cell.row.id) + 1}</div>
      ),
    },
    {
      Header: <Trans i18nKey="p.s.addoreditskincareroutine.image">Image</Trans>,
      accessor: 'imageUrl',
      disableSortBy: true,
      style: {width: '10%', textAlign: 'center'},
      Cell: ({cell: {value}}) => (
        <img
          alt=""
          src={value}
          style={{height: '50px', display: 'block', margin: '0 auto'}}
        />
      ),
    },
    {
      Header: (
        <Trans i18nKey="p.s.addoreditskincareroutine.product.name">
          Product Name
        </Trans>
      ),
      accessor: 'name',
      disableSortBy: true,
      style: {width: '10%', textAlign: 'center'},
      Cell: ({cell: {value}}) => (
        <div style={{textAlign: 'center'}}>{value}</div>
      ),
    },
    {
      Header: (
        <Trans i18nKey="p.s.addoreditskincareroutine.action.url">
          Action URL
        </Trans>
      ),
      accessor: 'actionUrl',
      disableSortBy: true,
      style: {width: '5%', textAlign: 'center'},
      Cell: ({cell: {value}}) =>
        !_isEmpty(value) ? (
          <div className="text-center kt-padding-10">
            <a
              href={value}
              target="_blank"
              className="cursor-pointer text-primary"
              style={{fontSize: '18px'}}
            >
              <i className="flaticon2-link" />
            </a>
          </div>
        ) : (
          <></>
        ),
    },
    {
      Header: (
        <Trans i18nKey="p.s.addoreditskincareroutine.creation">Creation</Trans>
      ),
      accessor: 'createdTime',
      disableSortBy: true,
      style: {width: '12.5%', textAlign: 'center'},
      Cell: ({cell: {value}}) => (
        <div className="text-right">
          <FormattedDate time={value} type={'long'} />
        </div>
      ),
    },
    {
      Header: (
        <Trans i18nKey="p.s.addoreditskincareroutine.modification">
          Modification
        </Trans>
      ),
      accessor: 'lastModified',
      disableSortBy: true,
      style: {width: '12.5%', textAlign: 'center'},
      Cell: ({cell: {value}}) => (
        <div className="text-right">
          <FormattedDate time={value} type={'long'} />
        </div>
      ),
    },
    {
      Header: (
        <Trans i18nKey="p.s.addoreditskincareroutine.action">Actions</Trans>
      ),
      id: 'actions',
      accessor: 'id',
      disableSortBy: true,
      style: {width: '5%', textAlign: 'center'},
      Cell: ({cell: {value}}) => (
        <div
          className="add-skincare-routine-list-page-action-button"
          style={{textAlign: 'center'}}
        >
          <ActionButton
            type={'delete'}
            onClick={async () => {
              setSelectedData(
                selectedData.filter(item => item.id !== parseInt(value))
              );
            }}
          />
        </div>
      ),
    },
  ];

  const validatedProductI18nInfo = data => {
    const newState = _cloneDeep(data);
    let hasIsDefault = false;

    if (!data.isDefault || data.isDefault.trim() === '') {
      toast.error(
        <Trans i18nKey="p.s.addoreditcategory.error.lang.unique">
          Please Check Default Language Settings
        </Trans>
      );
      return false;
    }

    for (const lang in newState.data.i18n) {
      const {productName} = newState.data.i18n[lang];
      const fieldName = t(
        'p.s.listskincareroutine.routine.Name',
        'Routine Name'
      );

      if (lang === data.isDefault && productName.trim() === '') {
        toast.error(
          <Trans i18nKey="p.s.addoreditcategory.error.default.lang.name.empty">
            Please check {{fieldName}} in the default language.
          </Trans>
        );
        return false;
      }

      if (productName.trim() === '') {
        delete newState.data.i18n[lang];
        continue;
      }

      newState.data.i18n[lang].isDefault = lang === data.isDefault;
      if (newState.data.i18n[lang].isDefault) {
        hasIsDefault = true;
      }
      const nameLength = productName.length,
        lengthLower = 2,
        lengthUpper = 200,
        itemLang = langUtils.getLanguageByValue(langCode.getAppLangList(), lang)
          .label;
      if (nameLength < lengthLower || nameLength > lengthUpper) {
        toast.error(
          <Trans i18nKey="toast.error.name.length2">
            {{fieldName}} in {{itemLang}} must be between {{lengthLower}} and{' '}
            {{lengthUpper}} characters.
          </Trans>
        );
        return false;
      }

      newState.data.i18n[lang].brandName = brandName;
    }

    if (!hasIsDefault) {
      toast.error(
        <Trans i18nKey="p.s.addoreditcategory.error.default.lang.no">
          Please Setting Default Language.
        </Trans>
      );
      return false;
    }

    return newState;
  };

  const formikConfig = {
    initialValues: initValue,

    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true);

      const apiValue = validatedProductI18nInfo(values);
      if (!apiValue) {
        setSubmitting(false);
        return;
      }
      delete apiValue.isDefault;

      apiValue.data.productRefs = selectedData.map((item, index) => {
        return {
          displayOrder: index,
          referencedProduct: {
            id: item.id,
          },
        };
      });

      productId
        ? await updateRoutineAPI(apiValue)
            .then(res => {
              if (
                res &&
                res.status === 'success' &&
                _get(res, 'errors', []).length === 0
              ) {
                toast.success(
                  <Trans i18nKey="toast.success.update.product">Updated!</Trans>
                );
                history.push('/skincare-pro/add-skincare/skincare-routine');
                setSubmitting(false);
                return;
              }
              toast.error(
                <Trans i18nKey="p.s.addoreditcategory.error.failed">
                  Failed
                </Trans>
              );
              setSubmitting(false);
            })
            .catch(e => {
              toast.error(
                <Trans i18nKey="p.s.addoreditcategory.error.failed">
                  Failed
                </Trans>
              );
              setSubmitting(false);
            })
        : await createRoutineAPI(apiValue)
            .then(res => {
              if (
                res &&
                res.status === 'success' &&
                _get(res, 'errors', []).length === 0
              ) {
                toast.success(
                  <Trans i18nKey="toast.success.update.product">
                    Created successfully.
                  </Trans>
                );
                history.push('/skincare-pro/add-skincare/skincare-routine');
                setSubmitting(false);
                return;
              }
              toast.error(
                <Trans i18nKey="p.s.addoreditcategory.error.failed">
                  Failed
                </Trans>
              );
              history.push('/skincare-pro/add-skincare/skincare-routine');
              setSubmitting(false);
            })
            .catch(e => {
              toast.error(
                <Trans i18nKey="p.s.addoreditcategory.error.failed">
                  Failed
                </Trans>
              );
              setSubmitting(false);
            });
    },
  };

  useEffect(() => {}, [selectedData]);

  if (
    !(
      privilegeUtils.canManageSku(accountSummary) ||
      privilegeUtils.isTechnicalSupport(accountSummary)
    )
  ) {
    history.push('/');
  }
  return isLoaded ? (
    <Loader />
  ) : (
    <>
      <div className="portlet-title">
        <h4>
          {!productId ? (
            <Trans i18nKey="p.s.addoreditskincareroutine.add.skincare.routine">
              Add Skincare Routine
            </Trans>
          ) : (
            <Trans i18nKey="p.s.addoreditskincareroutine.edit.skincare.routine">
              Edit Skincare Routine
            </Trans>
          )}
        </h4>
        <hr />
        <img
          alt="guid"
          src={require('../../../../../assets/images/help/help_start_guide.svg')}
          className="kt-margin-b-5 kt-margin-r-5"
        />{' '}
        <Trans i18nKey="p.s.addoreditskincareroutine.desc">
          This routine information will be available in all locations by
          default. If any location is not applicable, please modify the settings
          at Location Management.
        </Trans>
      </div>
      <br />
      <Formik {...formikConfig}>
        {formikProps => (
          <Form id="add-skincare-routine">
            <div className="portlet-body skincare-routine-layout">
              <div className="kt-portlet skincare-routine-layout-portlet">
                <div className="skincare-routine-layout-portlet-title">
                  <h5>
                    <Trans i18nKey="p.s.addoreditskincareroutine.basic.info">
                      Basic Info
                    </Trans>
                  </h5>
                  <hr />
                </div>
                <div className="skincare-routine-layout-portlet-body">
                  {productId && (
                    <div className="form-group row">
                      <label className="col col-form-label text-left">
                        <Trans i18nKey="p.s.listskincareroutine.routine.id">
                          Routine ID
                        </Trans>
                      </label>
                      <div className="col-9 d-flex align-items-center">
                        {productId}
                      </div>
                    </div>
                  )}
                  <div className="form-group row">
                    <label className="col-3 col-form-label text-left">
                      <InfoDialog
                        trigger={'click'}
                        placement="right"
                        title={
                          <Trans i18nKey="p.s.addoreditskincareroutine.default.language">
                            Default Language
                          </Trans>
                        }
                        description={
                          <Trans i18nKey="p.s.addoreditskincareroutine.default.language.desc">
                            Default Language will be displayed when the routine
                            name and description have not been translated in the
                            language selected by the customer.
                          </Trans>
                        }
                      >
                        <label className="cursor-help">
                          <Trans i18nKey="p.s.addoreditskincareroutine.default.language">
                            Default Language
                          </Trans>{' '}
                          <i className="fa fa-question-circle" />
                        </label>
                      </InfoDialog>
                    </label>
                    <div className="col-9">
                      <Field
                        name="isDefault"
                        options={langUtils.getAppLangList()}
                        component={SelectBox}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="kt-portlet skincare-routine-layout-portlet">
                <div className="skincare-routine-layout-portlet-title">
                  <h5>
                    <Trans i18nKey="p.s.addoreditskincareroutine.routine.details">
                      Routine Details (Localization)
                    </Trans>
                  </h5>
                  <hr />
                </div>
                <div className="skincare-routine-layout-portlet-body">
                  <div className="form-group row">
                    <div className="col-12">
                      <Field
                        name="data.i18n"
                        options={langUtils.getAppLangList()}
                        chooseLang={formikProps.values.isDefault}
                        showProductLang={
                          <InfoDialog
                            trigger={'click'}
                            placement="right"
                            title={
                              <Trans i18nKey="p.s.addoreditskincareproduct.product.lang">
                                Language of Name / Description
                              </Trans>
                            }
                            description={
                              <Trans i18nKey="p.s.addoreditskincareroutine.product.lang.desc">
                                Select a language to edit the corresponding
                                routine details below. Repeat to set up all
                                language versions for localization.
                              </Trans>
                            }
                          >
                            <label className="cursor-help">
                              <Trans i18nKey="p.s.addoreditskincareproduct.product.lang">
                                Language of Name / Description
                              </Trans>{' '}
                              <i className="fa fa-question-circle" />
                            </label>
                          </InfoDialog>
                        }
                        showProductName={
                          <Trans i18nKey="p.s.addoreditskincareroutine.routine.name">
                            Routine Name
                          </Trans>
                        }
                        placeholderName={t('c.f.text.max.character', {
                          defaultValue: 'Max. {{maxNum}} characters.',
                          maxNum: 200,
                        })}
                        labelWidth={3}
                        component={ProductSetting}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="kt-portlet skincare-routine-layout-portlet">
                <div className="skincare-routine-layout-portlet-title">
                  <h5>
                    <Trans i18nKey="p.s.addoreditskincareroutine.skincare.product">
                      Skincare Product
                    </Trans>
                  </h5>
                  <hr />
                </div>
                <div className="skincare-routine-layout-portlet-body">
                  <ControlBar setShowModal={setShowModal} />
                  <ChooseProductModal
                    showModal={showModal}
                    setShowModal={setShowModal}
                    selectedData={selectedData}
                    setSelectedData={setSelectedData}
                  />
                  <div style={{overflowX: 'scroll'}}>
                    <DragTable
                      data={selectedData}
                      columns={columns}
                      setData={setSelectedData}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="kt-margin-t-10 text-center">
              <SimpleButtonGroup
                onCancelClick={() =>
                  history.push('/skincare-pro/add-skincare/skincare-routine')
                }
                form={'add-skincare-routine'}
                isUpdating={true}
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
